import { brandAndProductOf, mostSpecificBrandPathOf } from '@atlas/core-utils/classification'
import { AxisCode } from '@atlas/types'
import type { WorkOrder } from '@/domain/work-order.types'
import { useBootstrapValues } from '@/hooks/useClassification'

/**
 * The Brand and Product rows, read back out of the order's `brand` tags: the most specific one,
 * since an order from Workfront carries the brand and its product side by side.
 *
 * There is no `product` axis: a product is a CHILD of its brand on the one `brand` axis,
 * and the create form collapses the pair to the most specific of the two (see
 * `tupleFromForm`). So an order with a product chosen is tagged `brand: ['prod_…']`, and
 * asking the tuple for a `product` key gets nothing — the Product row silently never
 * rendered while the Brand row showed the product's name.
 *
 * Resolved through `parentId`, not the `prod_` prefix or the label ordering: the level a
 * value sits at is what the registry says it is, and a code convention is not a guarantee.
 */
export function useBrandAndProduct(wo: WorkOrder): { brand?: string; product?: string } {
	const values = useBootstrapValues(AxisCode.BRAND)
	const { brandCode, productCode } = brandAndProductOf(mostSpecificBrandPathOf(values, wo.tuple?.[AxisCode.BRAND]))
	const labelOf = (code: string | null) => (code ? (values.find((v) => v.code === code)?.label ?? code) : undefined)
	return { brand: labelOf(brandCode), product: labelOf(productCode) }
}
